/* Gunrange shop — the availability component (snippets/gr-availability.liquid:
   the design's AvailabilityStatus, wired to the buy button). One controller
   per [data-gr-avail] root: the product page's buy block and the quick-add
   modal, which render the same markup.

   The stock check (Frankonia products only — the root says data-tracked):
     GET <data-proxy>?variant=<id>     (theme setting stock_proxy_path, /apps/stock)
     200 { stale, variants: { "<id>": { status, checked_at, live } } }
         status in_stock | backorder | out_of_stock. stale:true means it
         couldn't be confirmed live just now: shown, marked unconfirmed with
         how old it is.
     404 { "error": "not_tracked" }    not a Frankonia variant: Shopify's own
         availability (data-shopify), confirmed.
     anything else, a network error, no answer within data-timeout ms, or an
     answer without this variant: "couldn't confirm" — the last known status
     (data-fallback, from frankonia.availability), marked unconfirmed.
   Untracked products never check and never show the checking state.

   States: checking → in_stock | backorder | out_of_stock, each confirmed or
   unconfirmed; unavailable when the chosen options match no variant. The
   status line is a polite live region, so the answer is announced once.
   Buyable (in stock, backorder — unconfirmed ones too) enables the button and
   reveals the express checkout; sold out swaps the buy row for the
   back-in-stock action (gr-notify). The resolved status is written to the
   form's properties[_gr_availability] (the cart flags backorder lines).

   Answers are kept per variant for the page view: the product page and a
   quick add of the same variant share one request. Failures aren't kept, so
   the next look at that variant asks again.

   API (window.GR.availability):
     init(root) / get(root)     the controller for a [data-gr-avail] root
     markSoldOut(variantId)     Shopify refused the variant as sold out at
                                add to cart: show sold out wherever it's shown
   A controller: setVariant(data) (data-* of a re-rendered root: variant,
   tracked, shopify, fallback), pending() (a variant change is loading),
   state ({ variantId, state, confirmed, buyable }).
   Each render dispatches `gr:availability` (bubbles) with that state. */
(() => {
  'use strict';

  const GR = (window.GR = window.GR || {});
  if (GR.availability) return; // rendered once in the layout; guard a second copy anyway
  if (typeof GR.template !== 'function') {
    console.error('[gr-availability] gr-shop.js has not loaded; buy buttons fall back to plain form posts.');
    return;
  }

  const STATUSES = new Set(['in_stock', 'backorder', 'out_of_stock']);
  const BUYABLE = new Set(['in_stock', 'backorder']);
  const DEFAULT_TIMEOUT = 6000;

  class StockCheckError extends Error {}

  /* ── The request ─────────────────────────────────────────────────────── */
  const readJson = async (response) => {
    try {
      return await response.json();
    } catch (e) {
      return null; // not JSON (e.g. Shopify's HTML 404 when the app proxy isn't set up)
    }
  };

  const ask = async (proxy, variantId, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const url = `${proxy}${proxy.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variantId)}`;
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      const body = await readJson(response);
      if (response.status === 404 && body && body.error === 'not_tracked') return { kind: 'not_tracked' };
      if (!response.ok) {
        throw new StockCheckError(`HTTP ${response.status}${body && body.error ? ` (${body.error})` : ''}`);
      }
      const entry = body && body.variants ? body.variants[String(variantId)] : null;
      if (!entry) throw new StockCheckError('the answer has no status for this variant');
      if (!STATUSES.has(entry.status)) throw new StockCheckError(`unknown status "${entry.status}"`);
      const checkedAt = Date.parse(entry.checked_at);
      return {
        kind: 'answer',
        status: entry.status,
        confirmed: body.stale !== true,
        checkedAt: Number.isFinite(checkedAt) ? checkedAt : null
      };
    } catch (error) {
      if (error instanceof StockCheckError) throw error;
      if (error.name === 'AbortError') throw new StockCheckError(`no answer within ${timeoutMs} ms`);
      throw new StockCheckError(`request failed: ${error.message}`); // network error
    } finally {
      clearTimeout(timer);
    }
  };

  // variant id → answer (settled) and → Promise (in flight).
  const settled = new Map();
  const inFlight = new Map();

  const answerFor = (proxy, variantId, timeoutMs) => {
    if (!inFlight.has(variantId)) {
      const request = ask(proxy, variantId, timeoutMs)
        .then((answer) => {
          settled.set(variantId, answer);
          return answer;
        })
        .finally(() => {
          if (inFlight.get(variantId) === request) inFlight.delete(variantId);
        });
      inFlight.set(variantId, request);
    }
    return inFlight.get(variantId);
  };

  /* ── "last checked 12 minutes ago", in the page's language ──────────── */
  const ago = (timestamp) => {
    try {
      const format = new Intl.RelativeTimeFormat(document.documentElement.lang || undefined, { numeric: 'auto' });
      const minutes = Math.max(1, Math.round((Date.now() - timestamp) / 60000));
      if (minutes < 60) return format.format(-minutes, 'minute');
      const hours = Math.round(minutes / 60);
      if (hours < 48) return format.format(-hours, 'hour');
      return format.format(-Math.round(hours / 24), 'day');
    } catch (e) {
      return null; // no Intl.RelativeTimeFormat: the note without the time stays
    }
  };

  /* ── The controller ──────────────────────────────────────────────────── */
  const controllers = new Map(); // root → controller (roots removed from the page are pruned)

  class Availability {
    constructor(root) {
      this.root = root;
      this.scope = root.closest('[data-gr-product]') || root.parentElement;
      this.line = root.querySelector('[data-gr-avail-status]');
      this.notes = root.querySelector('[data-gr-avail-notes]');
      this.token = 0;
      this.current = null;
      // The no-JavaScript rendering goes; from here on the script paints every state.
      root.querySelectorAll('[data-gr-nojs]').forEach((node) => node.remove());
      this.scope.querySelectorAll('[data-gr-buy-row], [data-gr-buy], [data-gr-notify]').forEach((node) => {
        node.removeAttribute('data-gr-js');
        node.removeAttribute('data-gr-nojs');
      });
      this.check();
    }

    get data() {
      const d = this.root.dataset;
      const timeout = Number(d.timeout);
      return {
        variant: d.variant || '',
        tracked: d.tracked === 'true',
        shopify: STATUSES.has(d.shopify) ? d.shopify : 'out_of_stock',
        fallback: d.fallback,
        proxy: d.proxy || '',
        timeout: timeout > 0 ? timeout : DEFAULT_TIMEOUT
      };
    }

    get state() {
      return this.current;
    }

    async check() {
      const token = ++this.token;
      const { variant, tracked, shopify, fallback, proxy, timeout } = this.data;
      const own = { phase: 'resolved', status: shopify, confirmed: true, checkedAt: null };
      const lastKnown = { phase: 'resolved', status: STATUSES.has(fallback) ? fallback : shopify, confirmed: false, checkedAt: null };

      if (!variant) return this.render({ phase: 'unavailable' });
      if (!tracked) return this.render(own);
      if (!proxy) {
        console.error('[gr-availability] No stock check path (theme setting stock_proxy_path); showing the last known status.');
        return this.render(lastKnown);
      }

      const fromAnswer = (answer) =>
        answer.kind === 'not_tracked'
          ? own
          : { phase: 'resolved', status: answer.status, confirmed: answer.confirmed, checkedAt: answer.checkedAt };

      if (settled.has(variant)) return this.render(fromAnswer(settled.get(variant)));

      this.render({ phase: 'checking' });
      let view;
      try {
        view = fromAnswer(await answerFor(proxy, variant, timeout));
      } catch (error) {
        console.warn(`[gr-availability] Stock check for variant ${variant} failed (${error.message}); showing the last known status, unconfirmed.`);
        view = lastKnown;
      }
      if (token === this.token) this.render(view); // else the variant changed while we waited
    }

    setVariant(data) {
      ['variant', 'tracked', 'shopify', 'fallback'].forEach((key) => {
        this.root.dataset[key] = data[key] || '';
      });
      this.check();
    }

    // A variant change is loading: stop offering the old variant, the
    // express checkout included (it would buy the old one). render() brings
    // them back for the new variant when it's buyable.
    pending() {
      this.token++;
      if (this.data.tracked) {
        this.render({ phase: 'checking' });
        return;
      }
      const button = this.scope.querySelector('[data-gr-buy]');
      const express = this.scope.querySelector('[data-gr-express]');
      if (button) button.setAttribute('aria-disabled', 'true');
      if (express) express.hidden = true;
    }

    render(view) {
      const resolved = view.phase === 'resolved';
      const key = resolved ? view.status : view.phase; // checking | unavailable | in_stock | backorder | out_of_stock
      const unconfirmed = resolved && !view.confirmed;
      const buyable = resolved && BUYABLE.has(view.status);

      // The status line: same element (the live region), new content. A gift
      // card in stock reads as digital ("Always available").
      const line = key === 'in_stock' && this.root.dataset.digital === 'true' ? 'digital' : key;
      const fragment = GR.template(`avail-${line}${unconfirmed ? '-unconfirmed' : ''}`);
      const source = fragment && fragment.querySelector('.avail');
      if (this.line && source) {
        this.line.className = source.className;
        this.line.replaceChildren(...source.childNodes);
        const note = this.line.querySelector('[data-gr-avail-time]');
        const when = unconfirmed && view.checkedAt ? ago(view.checkedAt) : null;
        if (note && when) note.textContent = note.dataset.grAvailTime.replace('{time}', when);
      }

      // The notes: the backorder wait; what "couldn't confirm" means for an order.
      if (this.notes) {
        const names = [];
        if (resolved && view.status === 'backorder') names.push('avail-wait');
        if (unconfirmed && buyable) names.push('avail-why');
        this.notes.replaceChildren(...names.map((name) => GR.template(name)).filter(Boolean));
      }

      this.root.dataset.state = key;
      this.root.dataset.confirmed = String(!unconfirmed);

      // The buy controls in the same product.
      const row = this.scope.querySelector('[data-gr-buy-row]');
      const button = this.scope.querySelector('[data-gr-buy]');
      const notify = this.scope.querySelector('[data-gr-notify]');
      const express = this.scope.querySelector('[data-gr-express]');
      const property = this.scope.querySelector('[data-gr-avail-prop]');
      const soldOut = key === 'out_of_stock';

      if (row) row.hidden = soldOut;
      if (notify) notify.hidden = !soldOut;
      if (express) express.hidden = !buyable;
      if (button) {
        button.disabled = false; // the no-JavaScript "unavailable" button; aria-disabled from here on
        button.dataset.state = key === 'checking' ? 'checking' : buyable ? 'ready' : 'off';
        if (buyable) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', 'true');
        // gr-product.js owns the label while an add is in flight or just done.
        if (!button.hasAttribute('data-gr-busy')) {
          const label = GR.template(key === 'checking' ? 'buy-checking' : key === 'unavailable' ? 'buy-unavailable' : 'buy-add');
          if (label) button.replaceChildren(label);
        }
      }
      if (property) {
        const known = resolved && this.data.tracked;
        property.disabled = !known;
        property.value = known ? `${view.status}${unconfirmed ? '_unconfirmed' : ''}` : '';
      }

      this.current = { variantId: this.data.variant, state: key, confirmed: !unconfirmed, buyable };
      this.root.dispatchEvent(new CustomEvent('gr:availability', { bubbles: true, detail: this.current }));
    }
  }

  /* ── Wiring ──────────────────────────────────────────────────────────── */
  const prune = () => {
    controllers.forEach((_, root) => {
      if (!root.isConnected) controllers.delete(root);
    });
  };

  const init = (root) => {
    if (!controllers.has(root)) {
      prune();
      controllers.set(root, new Availability(root));
    }
    return controllers.get(root);
  };

  const initAll = (scope) => (scope || document).querySelectorAll('[data-gr-avail]').forEach(init);

  const markSoldOut = (variantId) => {
    const id = String(variantId);
    settled.set(id, { kind: 'answer', status: 'out_of_stock', confirmed: true, checkedAt: Date.now() });
    prune();
    controllers.forEach((controller) => {
      if (controller.data.variant === id) controller.check();
    });
  };

  GR.availability = { init, get: (root) => (root ? controllers.get(root) || null : null), markSoldOut };

  // A button that isn't buyable (checking, sold out, no such variant) never submits.
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-gr-buy][aria-disabled="true"]')) e.preventDefault();
  });
  document.addEventListener(
    'submit',
    (e) => {
      const button = e.target.querySelector && e.target.querySelector('[data-gr-buy]');
      if (button && button.getAttribute('aria-disabled') === 'true') e.preventDefault();
    },
    true
  );

  initAll();
  document.addEventListener('gr:quick-add-loaded', (e) => initAll(e.target));
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
